/**
 * Phase 2L-A101 — Ebook lead-magnet section.
 *
 * Self-contained two-column block. Used on the hidden review page
 * (`/home-ebook-review`) for now. Once Stephen approves, this same
 * component can be dropped into `Home.jsx` for the live homepage —
 * no other refactor required.
 *
 * Layout:
 *   • Desktop: 2 columns — 3D mockup left, headline + form right
 *   • Mobile: image first, then headline, then form
 *
 * Form: Name / Email / Suburb / State. State is a dropdown
 * (QLD/NSW/VIC/SA/WA/TAS/ACT/NT). Submit posts to
 * `/api/ebook-leads` and flips to a success state with the
 * download button + a secondary CTA back to /water-testing.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Download, FileText, Mail } from "lucide-react";
import { pushFormEvent } from "@/lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL || "";

const AU_STATES = ["QLD", "NSW", "VIC", "SA", "WA", "TAS", "ACT", "NT"];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EbookLeadMagnet({ sourceRoute = "/" }) {
  const [form, setForm] = useState({ name: "", email: "", suburb: "", state: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [submitError, setSubmitError] = useState("");

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: "" }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name";
    if (!EMAIL_RE.test(form.email.trim())) e.email = "Please enter a valid email";
    if (form.suburb.trim().length < 2) e.suburb = "Please enter your suburb";
    if (!AU_STATES.includes(form.state)) e.state = "Please choose a state";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/ebook-leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          suburb: form.suburb.trim(),
          state: form.state,
          source_route: sourceRoute,
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || "Something went wrong");
      }
      const data = await r.json();
      pushFormEvent("contact_form_submit", {
        form_name: "free_guide_download",
        form_type: "lead_magnet",
        campaign_source: sourceRoute || null,
      });
      setDownloadUrl(data.download_url || "/ebook/aware-tank-bore-water-testing-guide-v4.pdf");
    } catch (err) {
      setSubmitError(err.message || "Could not submit — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="ebook-lead-magnet"
      className="relative overflow-hidden bg-gradient-to-br from-cyan-50 via-white to-slate-50"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* 3D mockup */}
          <div className="order-1 lg:order-1 flex justify-center lg:justify-start">
            <div className="relative">
              <img
                src="/ebook/ebook-3d-cover-transparent.png"
                alt="The Australian Homeowner's Guide to Tank & Bore Water Testing — free AWARE Water Testing ebook"
                className="w-full max-w-[300px] sm:max-w-[410px] drop-shadow-[0_28px_45px_rgba(8,47,73,0.28)]"
                style={{ transform: "rotate(10deg)", transformOrigin: "center" }}
                loading="lazy"
                data-testid="ebook-3d-image"
              />
              <div className="absolute -inset-x-6 -bottom-4 h-8 bg-cyan-100/40 blur-2xl rounded-full pointer-events-none" />
            </div>
          </div>

          {/* Copy + form */}
          <div className="order-2 lg:order-2">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">
              Free Homeowner Guide · Australia-wide
            </div>
            <h2
              data-testid="ebook-headline"
              className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight"
            >
              Download the Free Australian Guide to Tank &amp; Bore Water Testing
            </h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              A practical guide for Australian homeowners relying on tank or
              bore water — understanding your supply, common water quality
              risks, and what to do next after a test result.
            </p>
            <p className="mt-3 text-base text-slate-600 leading-relaxed">
              Clear water is not always problem-free. This free guide explains
              what to test, when testing matters, how to read common results
              and the simple steps that help protect your household water
              supply.
            </p>

            <ul className="mt-6 space-y-2 text-slate-700">
              {[
                "Understand the most important checks for tank and bore water",
                "Learn when clear water can still need testing",
                "Know what E. coli, coliforms, pH, turbidity, hardness, iron and manganese may indicate",
                "See how mail-out sample kits work anywhere in Australia",
              ].map((b) => (
                <li key={b} className="flex gap-2 items-start text-[15px]">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-cyan-600" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            {/* Form OR success state */}
            <div className="mt-8 rounded-3xl bg-white ring-1 ring-slate-200 p-6 sm:p-7 shadow-sm">
              {!downloadUrl ? (
                <form onSubmit={onSubmit} data-testid="ebook-form" noValidate>
                  <h3 className="font-display text-xl font-bold text-slate-900">
                    Get Your Free Guide
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Enter your details below for instant access to the ebook.
                  </p>
                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field
                      label="Name"
                      testid="ebook-field-name"
                      value={form.name}
                      onChange={update("name")}
                      error={errors.name}
                      autoComplete="name"
                    />
                    <Field
                      label="Email"
                      type="email"
                      testid="ebook-field-email"
                      value={form.email}
                      onChange={update("email")}
                      error={errors.email}
                      autoComplete="email"
                    />
                    <Field
                      label="Suburb"
                      testid="ebook-field-suburb"
                      value={form.suburb}
                      onChange={update("suburb")}
                      error={errors.suburb}
                      autoComplete="address-level2"
                    />
                    <SelectField
                      label="State"
                      testid="ebook-field-state"
                      value={form.state}
                      onChange={update("state")}
                      error={errors.state}
                      options={AU_STATES}
                    />
                  </div>
                  {submitError && (
                    <div
                      data-testid="ebook-submit-error"
                      className="mt-3 text-sm text-red-600"
                    >
                      {submitError}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="ebook-submit"
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3.5 text-base transition disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <Mail className="w-4 h-4" />
                    {submitting ? "Sending…" : "Download Free Guide"}
                  </button>
                  <p className="mt-3 text-xs text-slate-500 leading-relaxed">
                    We'll only use your details in relation to your guide
                    download and relevant AWARE follow-up.
                  </p>
                </form>
              ) : (
                <div data-testid="ebook-success-state">
                  <div className="flex items-center gap-2 text-cyan-700">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="text-sm font-semibold uppercase tracking-wide">
                      Ready to download
                    </span>
                  </div>
                  <h3 className="mt-2 font-display text-2xl font-bold text-slate-900">
                    Thanks — your free guide is ready.
                  </h3>
                  <p className="mt-2 text-base text-slate-600">
                    Click below to download your copy of The Australian
                    Homeowner's Guide to Tank &amp; Bore Water Testing.
                  </p>
                  <a
                    href={downloadUrl}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="ebook-download-button"
                    className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3.5 text-base transition w-full"
                  >
                    <Download className="w-4 h-4" />
                    Download Your Free Guide
                  </a>
                  <div className="mt-5 pt-5 border-t border-slate-100">
                    <p className="text-sm text-slate-600">
                      Ready to test your water?
                    </p>
                    <Link
                      to="/book-water-test?kit=mail_out"
                      data-testid="ebook-secondary-mailout"
                      className="mt-2 inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold px-5 py-3 text-sm transition"
                    >
                      Order a mail-out water testing kit
                    </Link>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Link
                        to="/#water-tests"
                        data-testid="ebook-secondary-compare"
                        className="inline-flex items-center gap-1 text-sm font-semibold text-cyan-700 hover:text-cyan-800 underline-offset-2 hover:underline"
                      >
                        Compare AWARE test packages
                      </Link>
                      <span className="text-slate-300">·</span>
                      <Link
                        to="/book-water-test"
                        data-testid="ebook-secondary-book"
                        className="inline-flex items-center gap-1 text-sm font-semibold text-cyan-700 hover:text-cyan-800 underline-offset-2 hover:underline"
                      >
                        Book online and save 20%
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, type = "text", testid, value, onChange, error, autoComplete }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700">{label}</span>
      <input
        type={type}
        required
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        data-testid={testid}
        className={`mt-1 block w-full rounded-xl border ${error ? "border-red-300" : "border-slate-300"} bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition`}
      />
      {error && <span className="mt-1 block text-xs text-red-600" data-testid={`${testid}-error`}>{error}</span>}
    </label>
  );
}

function SelectField({ label, testid, value, onChange, error, options }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700">{label}</span>
      <select
        required
        value={value}
        onChange={onChange}
        data-testid={testid}
        className={`mt-1 block w-full rounded-xl border ${error ? "border-red-300" : "border-slate-300"} bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition`}
      >
        <option value="">Choose…</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
      {error && <span className="mt-1 block text-xs text-red-600" data-testid={`${testid}-error`}>{error}</span>}
    </label>
  );
}
